import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Downloading bills from the Bills page (LB-6033): the list as a CSV, or the bills' files as a zip. The words and
 * the rules about what needs saying before it starts live here, apart from the requests, so they can be read and
 * tested on their own.
 */

export const DOWNLOAD_KINDS = {
  csv: 'Csv',
  files: 'Files'
};

/**
 * The two downloads, each with its icon. The footer's covers several bills; a row's menu and a bill's details cover one,
 * so they name its one file.
 */
export const DOWNLOAD_OPTIONS = [{
  kind: DOWNLOAD_KINDS.csv,
  label: 'Export list (CSV)',
  oneBillLabel: 'Export list (CSV)',
  icon: 'table'
}, {
  kind: DOWNLOAD_KINDS.files,
  label: 'Download files (zip)',
  oneBillLabel: 'Download file (zip)',
  icon: 'file-download'
}];

/** The service's word for a bill the page calls Processed. Here rather than imported, so this module needs nothing. */
const PROCESSED_STATUS = 'Validated';

/** The service's words for the bills a download leaves out: duplicates, rejected and error bills. */
const SKIPPED_STATUSES = ['Duplicate', 'Rejected', 'Failed'];

/**
 * The most files the page asks for in one zip. The service holds up to 200 and refuses more; asking for fewer here
 * keeps a download to something a person can sensibly open.
 */
export const PAGE_FILE_LIMIT = 100;
const plural = function (count, one) {
  let many = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "".concat(one, "s");
  return "".concat(count, " ").concat(count === 1 ? one : many);
};
export function downloadScopeLabel(_ref) {
  let selectedCount = _ref.selectedCount,
    pageProcessedCount = _ref.pageProcessedCount;
  return selectedCount > 0 ? plural(selectedCount, 'selected bill') : "".concat(plural(pageProcessedCount, 'processed bill'), " on this page");
}

/** Not every bill the filters match: the product's choice (LB-6033), so a download never reaches past the screen. */
export function pageProcessedBillIds(rows) {
  return rows.filter(row => row.extractStatus === PROCESSED_STATUS).map(row => row.id);
}

/**
 * Why one bill can't be downloaded, or null. Said on its own menu, where asking the service would only come back with
 * nothing to download. A bill whose status isn't known here is left to the service.
 */
export function billDownloadBlockedReason(extractStatus) {
  return SKIPPED_STATUSES.includes(extractStatus) ? "Duplicate, rejected and error bills can't be downloaded." : null;
}

/** Why the Download button is held back, or null. A selection is the person's own, so only the page can be stale. */
export function downloadDisabledReason(_ref2) {
  let hasSelection = _ref2.hasSelection,
    isPageCurrent = _ref2.isPageCurrent,
    pageProcessedIds = _ref2.pageProcessedIds;
  if (hasSelection) return null;
  if (!isPageCurrent) return 'Loading this page…';
  return pageProcessedIds.length === 0 ? 'No processed bills on this page.' : null;
}

/**
 * What has to be said before a download starts, or null when it can simply start. Refusals come back with
 * `canConfirm: false`: nothing would be downloaded, so the only thing to do is close.
 *
 * @param requestedCount how many bills were asked for, so a refusal for one bill speaks of that bill
 */
export function downloadDialog(preview, kind) {
  var _preview$skipped$tota, _preview$skipped, _preview$billsWithout;
  let requestedCount = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  const isFiles = kind === DOWNLOAD_KINDS.files;
  const isOneBill = requestedCount === 1;
  const skipped = (_preview$skipped$tota = (_preview$skipped = preview.skipped) === null || _preview$skipped === void 0 ? void 0 : _preview$skipped.total) !== null && _preview$skipped$tota !== void 0 ? _preview$skipped$tota : 0;
  const nothing = title => ({
    title,
    confirmLabel: 'OK',
    canConfirm: false
  });
  if (preview.billCount === 0) {
    let message = 'None of these bills can be downloaded.';
    if (isOneBill) {
      message = skipped ? "It's a duplicate, rejected or error bill, which is left out." : "This bill can't be downloaded.";
    } else if (skipped) {
      message = 'These are duplicate, rejected or error bills, which are left out.';
    }
    return _objectSpread(_objectSpread({}, nothing('Nothing to download')), {}, {
      message
    });
  }
  if (isFiles && preview.fileCount === 0) {
    return _objectSpread(_objectSpread({}, nothing('Nothing to download')), {}, {
      message: isOneBill ? 'This bill has no file to download.' : 'None of these bills has a file to download.'
    });
  }
  if (isFiles && preview.fileCount > PAGE_FILE_LIMIT) {
    return _objectSpread(_objectSpread({}, nothing('Too many files')), {}, {
      message: "These bills come to ".concat(plural(preview.fileCount, 'file'), ". Download up to ").concat(PAGE_FILE_LIMIT, " files at a time: ") + 'narrow the filters or the selection.'
    });
  }
  const inBackground = isFiles && preview.inBackground;
  const withoutFile = isFiles ? (_preview$billsWithout = preview.billsWithoutFile) !== null && _preview$billsWithout !== void 0 ? _preview$billsWithout : 0 : 0;
  if (!skipped && !inBackground && !withoutFile) return null;
  const parts = [];
  if (skipped) {
    parts.push("".concat(plural(skipped, 'duplicate, rejected or error bill'), " will be skipped."));
  }
  if (withoutFile) {
    parts.push("".concat(plural(withoutFile, 'bill has', 'bills have'), " no file, so ").concat(withoutFile === 1 ? "it won't" : "they won't", " be in the zip."));
  }
  if (inBackground) {
    parts.push("That's ".concat(plural(preview.fileCount, 'file'), ", so we'll prepare the zip and email you a link when it's ready."));
  }
  return {
    title: isFiles ? 'Download files?' : 'Export list?',
    message: parts.join(' '),
    confirmLabel: inBackground ? 'Prepare and email' : isFiles ? 'Download zip' : 'Export CSV',
    canConfirm: true
  };
}

/**
 * The service's own words for a download it refused (400) or could not start (503), which it writes for this page;
 * anything else is said plainly, as an unexpected failure's text is not for a customer to read.
 */
export function downloadErrorMessage(err) {
  var _err$response, _err$response2;
  const message = err === null || err === void 0 || (_err$response = err.response) === null || _err$response === void 0 || (_err$response = _err$response.data) === null || _err$response === void 0 ? void 0 : _err$response.message;
  const isOwnWords = [400, 503].includes(err === null || err === void 0 || (_err$response2 = err.response) === null || _err$response2 === void 0 ? void 0 : _err$response2.status);
  return isOwnWords && typeof message === 'string' && message.length > 0 && message.length <= 300 ? message : "The download couldn't be prepared. Try again in a moment.";
}

/**
 * Starts a download from a short-lived link. The link names the file as an attachment, so the browser saves it rather
 * than leaving the page, and an anchor rather than `window.open` keeps a popup blocker out of it.
 */
export function triggerDownload(url) {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}